"use client"

import { ChakraProvider, defaultSystem } from "@chakra-ui/react"
import {
    ColorModeProvider,
    type ColorModeProviderProps,
} from "./color-mode"
import { createTheme, system } from "./theme"
import { ColorPaletteProvider, useColorPalette } from "./color-palette-context";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";

// Define the shape of our context state
interface ThemeContextType {
    customThemeColors: Record<string, string>;
    setCustomColor: (key: string, color: string) => void;
}

// Initialize the context with a default (or null) value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Initial custom colors for the theme
const initialCustomColors: Record<string, string> = {
    '50': '#ffffff',
    '100': '#f7f7f7',
    '200': '#f0f0f0',
    '300': '#adadad',
    '400': '#919191',
    '500': '#737373', 
    '600': '#525252',
    '700': '#9e9e9e',
    '800': '#383838',
    '900': '#141414',
    '950': '#000000',
};

interface ThemeProviderProps {
    children: ReactNode;
}

export function Provider(props: ColorModeProviderProps) {
    const { colorPalette } = useColorPalette();
    const [customColors, setCustomColors] = useState<Record<string, string>>(initialCustomColors);

    // Function to update a specific custom color shade
    const setCustomColor = (key: string, color: string) => {
        setCustomColors(prevColors => ({
            ...prevColors,
            [key]: color,
        }));
    };

    // The value provided by the context
    const contextValue = useMemo(() => ({
        customThemeColors: customColors,
        setCustomColor,
    }), [customColors]);

    const theme = useMemo(() => {
        return system(colorPalette, customColors);
        //return system(colorPalette, customThemeColors);
    }, [colorPalette, customColors]);

    return (
        <ThemeContext.Provider value={contextValue}>
            <ChakraProvider children={null} value={theme} > { /* Compiler is bugging tf out here, look into when I have more time */}
                <ColorModeProvider {...props} />
            </ChakraProvider>
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
